import { fetchAllRows } from '../fetchAllRows';
import { supabase } from '@/lib/supabase';
import { ORDER_STATUSES_WITH_PENDING_STOCK } from '@/lib/orderStatus';

// Helper seguro para detectar producción en Vite/Browser
const isProduction = () => {
  try {
    // Vite expone import.meta.env.PROD
    // Fallback a process.env solo si existe
    return (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD) ||
           (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production');
  } catch (_) {
    return false;
  }
};
import { sanitizeProductData, sanitizeInventoryData } from '@/lib/utils';

// Cache para almacenar datos en memoria
let inventoryCache = null;
let productsCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Función para limpiar el caché de productos
export const clearProductsCache = () => {
  productsCache = null;
};

export const fetchInventoryOptimized = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Usar caché si está disponible y no ha expirado
  if (!forceRefresh && inventoryCache && (now - lastFetchTime) < CACHE_DURATION) {
    return inventoryCache;
  }

  try {
    // Paginación para evitar límite de 1000 filas de PostgREST
    const PAGE_SIZE = 1000;
    let offset = 0;
    let allRows = [];
    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('inventory')
        .select(`
          id,
          product_id,
          size,
          color,
          quantity,
          pending_orders,
          created_at,
          updated_at
        `)
        .order('product_id', { ascending: true })
        .order('id', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      allRows = allRows.concat(page || []);
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Sanitizar datos antes de cachear
    const sanitizedData = sanitizeInventoryData(allRows || []);
    inventoryCache = sanitizedData;
    lastFetchTime = now;
    try {
      const debugId = (typeof localStorage !== 'undefined' && localStorage.getItem('inventoryDebugId')) || 'a5006d2b-8a31-444b-8c13-99fa2798ce08';
      const hasDebug = sanitizedData.some(r => String(r.product_id) === String(debugId));
      const sample = sanitizedData.slice(0, 3);
      if (!isProduction()) {
        // eslint-disable-next-line no-console
        console.log('🔎 INV-DEBUG fetchInventoryOptimized', { count: sanitizedData.length, hasDebug, debugId, sample, pages: Math.ceil((allRows.length || 0) / PAGE_SIZE) });
      }
    } catch (_) {}
    return inventoryCache;
  } catch (error) {
    console.error('Error fetching inventory:', error);
    throw error;
  }
};

export const fetchProductsOptimized = async (forceRefresh = false) => {
  if (!forceRefresh && productsCache) {
    return productsCache;
  }

  try {
    const data = await fetchAllRows(() => supabase
      .from('products')
      .select('id, code, description, price, temporada, sizes, colors, images, main_image_index, active, created_at, updated_at')
      .eq('active', true)
      .order('code', { ascending: true })
      .order('id', { ascending: true }));

    // Sanitizar datos antes de cachear
    const sanitizedData = (data || []).map(product => sanitizeProductData(product));
    productsCache = sanitizedData;
    
    if (!isProduction()) {
      console.log('📊 Diagnóstico de productos:');
      console.log(`- Total de productos activos: ${sanitizedData.length}`);
    }
    
    return productsCache;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función separada para obtener imágenes de productos cuando sea necesario
export const fetchProductImages = async (productIds) => {
  if (!productIds || productIds.length === 0) return {};
  
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id, images, main_image_index')
      .in('id', productIds);

    if (error) throw error;

    const imagesMap = {};
    data?.forEach(product => {
      try {
        const sanitizedProduct = sanitizeProductData(product);
        imagesMap[product.id] = {
          images: sanitizedProduct.images || [],
          main_image_index: sanitizedProduct.main_image_index || 0
        };
      } catch (productError) {
        console.warn('Error sanitizing product images for product', product.id, productError);
        imagesMap[product.id] = {
          images: [],
          main_image_index: 0
        };
      }
    });

    return imagesMap;
  } catch (error) {
    console.error('Error fetching product images:', error);
    return {};
  }
};

export const fetchPendingOrdersOptimized = async () => {
  try {
    // Tomar exclusivamente líneas con pendiente > 0 (paginado)
    const PAGE_SIZE = 1000;
    let offset = 0;
    let allItems = [];
    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('order_items')
        .select('id, order_id, product_id, size, color, pending_quantity, orders!inner(status)')
        .gt('pending_quantity', 0)
        .in('orders.status', ORDER_STATUSES_WITH_PENDING_STOCK)
        .order('id', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      allItems = allItems.concat(page || []);
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Agrupar por order_id para mantener el formato esperado aguas abajo
    const byOrder = new Map();
    for (const it of (allItems || [])) {
      const list = byOrder.get(it.order_id) || [];
      list.push({
        id: it.id,
        product_id: it.product_id,
        size: it.size,
        color: it.color,
        pending_quantity: Number(it.pending_quantity) || 0
      });
      byOrder.set(it.order_id, list);
    }

    const normalized = Array.from(byOrder.entries()).map(([orderId, lines]) => ({ id: orderId, items: lines }));

    if (!isProduction()) {
      console.log('📊 Diagnóstico de líneas con pendiente (>0)');
      console.log(`- Líneas con pendiente > 0 (total): ${allItems.length}`);
      console.log(`- Pedidos con líneas pendientes: ${normalized.length}`);
    }
    
    return normalized;
  } catch (error) {
    console.error('Error fetching pending orders:', error);
    throw error;
  }
};

// Función optimizada para recálculo por lotes
export const recalculateInventoryOptimized = async (progressCallback = null) => {
  progressCallback?.('Reconciliando movimientos en el servidor...', 10);
  const { data, error } = await supabase.rpc('reconcile_inventory_transaction');
  if (error) throw error;
  clearInventoryCache();
  progressCallback?.('Reconciliación completada', 100);
  return data;
};

// Función para obtener estadísticas rápidas del inventario
export const getInventoryStats = async () => {
  try {
    const data = await fetchAllRows(() => supabase
      .from('inventory')
      .select('quantity, pending_orders, product:products(price)')
      .order('id', { ascending: true }));

    const stats = {
      totalStock: 0,
      totalPending: 0,
      totalValue: 0,
      itemsWithStock: 0,
      itemsWithPending: 0
    };

    data?.forEach(item => {
      const quantity = parseInt(item.quantity) || 0;
      const pending = parseInt(item.pending_orders) || 0;
      const price = item.product?.price || 0;

      stats.totalStock += quantity;
      stats.totalPending += pending;
      stats.totalValue += quantity * price;
      
      if (quantity > 0) stats.itemsWithStock++;
      if (pending > 0) stats.itemsWithPending++;
    });

    return stats;
  } catch (error) {
    console.error('Error getting inventory stats:', error);
    throw error;
  }
};

// Función para limpiar caché
export const clearInventoryCache = () => {
  inventoryCache = null;
  productsCache = null;
  lastFetchTime = 0;
};

// Función para actualizar pending_orders en inventario de manera atómica
export const updateInventoryPendingOrders = async (updates, operation = 'replace') => {
  const { data, error } = await supabase.rpc('update_inventory_pending_orders', {
    updates_data: updates, operation_type: operation,
  });
  if (error) throw error;
  clearInventoryCache();
  return data;
};
